import { Head, Link, useForm } from '@inertiajs/react'
import { FormEvent, useCallback } from 'react'
import { ArrowLeft } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import SettingsNav from '@/components/SettingsNav'
import MacroEditor from '@/components/editor/MacroEditor'
import { stripHtml } from '@/lib/sanitizeHtml'
import { uploadImage } from '@/lib/imageUpload'

interface Macro {
  id: number
  name: string
  content: string
  usage_count: number
  created_at: string
  updated_at: string
}

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface MacrosFormProps {
  macro: Macro | null
  currentAgent: Agent
}

export default function MacrosForm({ macro, currentAgent }: MacrosFormProps) {
  const isEditing = !!macro

  const { data, setData, post, put, processing, errors } = useForm({
    name: macro?.name || '',
    content: macro?.content || '',
  })

  const handleImageUpload = useCallback(async (file: File): Promise<string> => {
    const result = await uploadImage(file)
    return result.url
  }, [])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (isEditing && macro) {
      put(`/macros/${macro.id}`)
    } else {
      post('/macros')
    }
  }

  return (
    <>
      <Head title={isEditing ? 'Editar Macro' : 'Novo Macro'} />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="settings" />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6">
            <Link
              href="/macros"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="text-lg font-semibold text-gray-900">Configurações</h1>
          </header>

          <div className="flex-1 flex min-h-0">
            <SettingsNav activePage="macros" isAdmin={currentAgent.role === 'admin'} />

            <main className="flex-1 overflow-auto p-6">
              <div className="max-w-2xl">
                <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Nome
                      </label>
                      <input
                        id="name"
                        type="text"
                        value={data.name}
                        onChange={(e) => setData('name', e.target.value)}
                        placeholder="ex: boas-vindas, despedida"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Digite /{data.name || 'nome'} no chat para usar este macro
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Conteúdo
                      </label>
                      <MacroEditor
                        value={data.content}
                        onChange={(html) => setData('content', html)}
                        placeholder="Digite o texto que será inserido ao usar este macro..."
                        onImageUpload={handleImageUpload}
                      />
                      {errors.content && (
                        <p className="mt-1 text-sm text-red-600">{errors.content}</p>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        Selecione texto para ver opções de formatação. Use o botão "Inserir variável" para adicionar dados dinâmicos.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                    <Link
                      href="/macros"
                      className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </Link>
                    <button
                      type="submit"
                      disabled={processing || !data.name.trim() || !stripHtml(data.content).trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isEditing ? 'Atualizar Macro' : 'Criar Macro'}
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  )
}
